import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { Edit2, Save, X, Download, Search, Trash2, ChevronLeft, ChevronRight, RefreshCw, FileJson, FileSpreadsheet, ImageOff, Image as ImageIcon } from 'lucide-react';
import { RoadSign, ROAD_SIGN_CODES } from '../../components/RoadSign';

const THEMES = [
  { code: '', name: 'Tous les thèmes' },
  { code: 'R', name: 'R · La route' },
  { code: 'C', name: 'C · La circulation' },
  { code: 'L', name: 'L · Le conducteur' },
  { code: 'U', name: 'U · Les autres usagers' },
  { code: 'D', name: 'D · Notions diverses' },
  { code: 'M', name: 'M · Mécanique' },
  { code: 'P', name: 'P · Équipements' },
  { code: 'E', name: 'E · Environnement' },
  { code: 'S', name: 'S · Premiers secours' }
];
const DIFFS = [
  { value: '', label: 'Toutes difficultés' },
  { value: 'facile', label: 'Facile' },
  { value: 'standard', label: 'Standard' },
  { value: 'difficile', label: 'Difficile' }
];
const SIGN_FILTERS = [
  { value: '', label: 'Tous (avec ou sans panneau)' },
  { value: 'yes', label: 'Avec panneau uniquement' },
  { value: 'no', label: 'Sans panneau uniquement' },
];
const PAGE_SIZE = 20;

export default function AdminQuestions() {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [search, setSearch] = useState('');
  const [hasSign, setHasSign] = useState('');
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminQuestions({
        theme: theme || undefined,
        difficulty: difficulty || undefined,
        search: search || undefined,
        has_sign: hasSign || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE
      });
      setQuestions(res.questions || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [theme, difficulty, search, hasSign, page]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const onFilterChange = (fn) => { fn(); setPage(0); };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditDraft({
      question: q.question || '',
      choices: [...(q.choices || ['', '', '', ''])],
      correct_answer: q.correct_answer || 'A',
      explanation: q.explanation || '',
      explanation_rich: q.explanation_rich || '',
      legal_reference: q.legal_reference || '',
      official_subtheme: q.official_subtheme || '',
      pitfall: q.pitfall || '',
      mnemonic: q.mnemonic || '',
      sign_code: q.sign_code || '',
      difficulty: q.difficulty || 'standard',
      theme_code: q.theme_code || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async (id) => {
    try {
      await api.updateAdminQuestion(id, editDraft);
      toast.success('Question mise à jour');
      setEditingId(null);
      setEditDraft(null);
      fetchQuestions();
    } catch (e) {
      toast.error('Échec de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await api.deleteAdminQuestion(id);
      toast.success('Question supprimée');
      fetchQuestions();
    } catch (e) {
      toast.error('Échec de la suppression');
    }
  };

  const handleExport = async (format) => {
    try {
      const blob = await api.exportAdminQuestions(format, theme || '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_export_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(`Échec export ${format}`);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const removeSign = async (q) => {
    if (!window.confirm(`Retirer le panneau ${q.sign_code} de cette question ?`)) return;
    try {
      await api.updateAdminQuestion(q.id, { sign_code: '' });
      toast.success('Panneau retiré');
      fetchQuestions();
    } catch (e) {
      toast.error('Échec du retrait du panneau');
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-questions-page">
      <div>
        <h1 className="text-3xl font-black">Questions QCM</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total} questions au total · Édition inline · Export JSON / CSV
        </p>
      </div>

      {/* Filtres + Actions */}
      <div className="mc-card p-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => onFilterChange(() => setSearch(e.target.value))}
              data-testid="admin-q-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
            />
          </div>
          <select
            value={theme}
            onChange={(e) => onFilterChange(() => setTheme(e.target.value))}
            data-testid="admin-q-theme"
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
          >
            {THEMES.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
          <select
            value={difficulty}
            onChange={(e) => onFilterChange(() => setDifficulty(e.target.value))}
            data-testid="admin-q-difficulty"
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
          >
            {DIFFS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <select
            value={hasSign}
            onChange={(e) => onFilterChange(() => setHasSign(e.target.value))}
            data-testid="admin-q-has-sign"
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
          >
            {SIGN_FILTERS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <div className="flex gap-2">
            <Button onClick={() => handleExport('json')} variant="outline" size="sm" className="flex-1" data-testid="admin-q-export-json">
              <FileJson className="w-4 h-4 mr-1" /> JSON
            </Button>
            <Button onClick={() => handleExport('csv')} variant="outline" size="sm" className="flex-1" data-testid="admin-q-export-csv">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="mc-card p-10 text-center" data-testid="admin-q-loading">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[hsl(var(--primary))] mb-2" />
          <p className="text-sm text-muted-foreground">Chargement…</p>
        </div>
      ) : questions.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="admin-q-empty">
          Aucune question ne correspond aux filtres.
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => {
            const isEditing = editingId === q.id;
            return (
              <div key={q.id} className="mc-card p-4" data-testid={`admin-q-${q.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{q.id}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">Thm {q.theme_code}</span>
                    <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-muted">{q.difficulty}</span>
                  </div>
                  <div className="flex gap-1">
                    {isEditing ? (
                      <>
                        <Button onClick={() => saveEdit(q.id)} size="sm" className="mc-btn-primary h-8" data-testid={`save-${q.id}`}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
                        </Button>
                        <Button onClick={cancelEdit} variant="outline" size="sm" className="h-8">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => startEdit(q)} variant="outline" size="sm" className="h-8" data-testid={`edit-${q.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button onClick={() => handleDelete(q.id)} variant="outline" size="sm" className="h-8" data-testid={`delete-${q.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-[#b42318]" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editDraft.question}
                      onChange={(e) => setEditDraft({ ...editDraft, question: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
                      data-testid={`edit-question-${q.id}`}
                    />
                    <div className="grid sm:grid-cols-2 gap-2">
                      {editDraft.choices.map((c, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={cn('w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0',
                            String.fromCharCode(65 + i) === editDraft.correct_answer ? 'bg-[hsl(var(--success))] text-white' : 'bg-muted')}>
                            {String.fromCharCode(65 + i)}
                          </span>
                          <input
                            type="text"
                            value={c}
                            onChange={(e) => {
                              const nc = [...editDraft.choices];
                              nc[i] = e.target.value;
                              setEditDraft({ ...editDraft, choices: nc });
                            }}
                            className="flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
                            data-testid={`edit-choice-${q.id}-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <select
                        value={editDraft.correct_answer}
                        onChange={(e) => setEditDraft({ ...editDraft, correct_answer: e.target.value })}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        data-testid={`edit-correct-${q.id}`}
                      >
                        {['A', 'B', 'C', 'D'].map(l => <option key={l} value={l}>Bonne réponse : {l}</option>)}
                      </select>
                      <select
                        value={editDraft.difficulty}
                        onChange={(e) => setEditDraft({ ...editDraft, difficulty: e.target.value })}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      >
                        {['facile', 'standard', 'difficile'].map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select
                        value={editDraft.theme_code}
                        onChange={(e) => setEditDraft({ ...editDraft, theme_code: e.target.value })}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      >
                        {THEMES.filter(t => t.code).map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
                      </select>
                    </div>

                    {/* SÉLECTEUR DE PANNEAU avec aperçu live */}
                    <div className="rounded-lg border border-border bg-[hsl(var(--panel))]/40 p-3 flex items-center gap-3" data-testid={`edit-sign-block-${q.id}`}>
                      <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center rounded-lg bg-card border border-border">
                        {editDraft.sign_code ? (
                          <RoadSign code={editDraft.sign_code} size={64} />
                        ) : (
                          <ImageOff className="w-8 h-8 text-muted-foreground/40" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                          Panneau de signalisation associé
                        </label>
                        <select
                          value={editDraft.sign_code}
                          onChange={(e) => setEditDraft({ ...editDraft, sign_code: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                          data-testid={`edit-sign-${q.id}`}
                        >
                          <option value="">— Aucun panneau —</option>
                          <optgroup label="Danger (A)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('A')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Intersections (AB)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('AB')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Interdiction (B)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('B') && !c.startsWith('B14') && !c.startsWith('B21') && !c.startsWith('B22') && !c.startsWith('AB')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Limitations vitesse (B14)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('B14')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Obligation (B21/B22)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('B21') || c.startsWith('B22')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Indication (C)">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('C')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                          <optgroup label="Feux tricolores">
                            {ROAD_SIGN_CODES.filter(c => c.startsWith('FEU_')).map(c => <option key={c} value={c}>{c}</option>)}
                          </optgroup>
                        </select>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {editDraft.sign_code
                            ? `Code: ${editDraft.sign_code} — l'aperçu se met à jour en temps réel`
                            : 'Sélectionnez un panneau ou laissez vide pour aucun visuel'}
                        </p>
                      </div>
                    </div>

                    {/* Encart pédagogique enrichi */}
                    <details className="rounded-lg border border-border bg-[hsl(var(--panel))]/40 p-3">
                      <summary className="text-xs font-black uppercase tracking-widest text-muted-foreground cursor-pointer">
                        Enrichissement pédagogique (référence légale, piège, à retenir)
                      </summary>
                      <div className="space-y-2 mt-3">
                        <textarea
                          value={editDraft.explanation_rich}
                          onChange={(e) => setEditDraft({ ...editDraft, explanation_rich: e.target.value })}
                          rows={3}
                          placeholder="Explication enrichie (250-400 caractères, citant l'article)"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
                          data-testid={`edit-explanation-rich-${q.id}`}
                        />
                        <div className="grid sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={editDraft.legal_reference}
                            onChange={(e) => setEditDraft({ ...editDraft, legal_reference: e.target.value })}
                            placeholder="Article R413-2"
                            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            data-testid={`edit-legal-${q.id}`}
                          />
                          <input
                            type="text"
                            value={editDraft.official_subtheme}
                            onChange={(e) => setEditDraft({ ...editDraft, official_subtheme: e.target.value })}
                            placeholder="R.3 Limitations de vitesse"
                            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            data-testid={`edit-subtheme-${q.id}`}
                          />
                        </div>
                        <textarea
                          value={editDraft.pitfall}
                          onChange={(e) => setEditDraft({ ...editDraft, pitfall: e.target.value })}
                          rows={2}
                          placeholder="Piège classique (80-150 caractères)"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                          data-testid={`edit-pitfall-${q.id}`}
                        />
                        <input
                          type="text"
                          value={editDraft.mnemonic}
                          onChange={(e) => setEditDraft({ ...editDraft, mnemonic: e.target.value })}
                          placeholder="À retenir / mnémonique (60-120 caractères)"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                          data-testid={`edit-mnemonic-${q.id}`}
                        />
                      </div>
                    </details>

                    <textarea
                      value={editDraft.explanation}
                      onChange={(e) => setEditDraft({ ...editDraft, explanation: e.target.value })}
                      rows={2}
                      placeholder="Explication courte (legacy)"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3 mb-2">
                      {q.sign_code && (
                        <div className="flex-shrink-0 flex flex-col items-center gap-1" data-testid={`q-sign-${q.id}`}>
                          <div className="w-16 h-16 flex items-center justify-center rounded-lg border border-border bg-card">
                            <RoadSign code={q.sign_code} size={56} />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[9px] font-mono font-black text-muted-foreground">{q.sign_code}</span>
                            <button
                              onClick={() => removeSign(q)}
                              className="text-[#b42318] hover:bg-[#fff1f2] rounded p-0.5 transition-colors"
                              title="Retirer ce panneau"
                              data-testid={`remove-sign-${q.id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      <p className="text-sm font-semibold flex-1">{q.question}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-1 text-xs">
                      {(q.choices || []).map((c, i) => {
                        const isRight = String.fromCharCode(65 + i) === q.correct_answer;
                        return (
                          <div key={i} className={cn('flex items-start gap-1.5 p-1.5 rounded', isRight && 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] font-bold')}>
                            <span className="font-black">{String.fromCharCode(65 + i)}.</span>
                            <span>{c}</span>
                          </div>
                        );
                      })}
                    </div>
                    {q.official_subtheme && (
                      <div className="text-[10px] mt-2">
                        <span className="inline-block rounded-full bg-muted px-2 py-0.5 font-mono font-bold">{q.official_subtheme}</span>
                        {q.legal_reference && <span className="ml-2 text-muted-foreground">{q.legal_reference}</span>}
                      </div>
                    )}
                    {(q.explanation_rich || q.explanation) && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{q.explanation_rich || q.explanation}</p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="admin-q-pagination">
          <Button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} variant="outline" size="sm">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-bold">
            Page {page + 1} / {totalPages}
          </span>
          <Button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} variant="outline" size="sm">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
